import { Link } from "react-router-dom";

import { type LatestCliDownload, useLatestCliDownload } from "../useLatestCliDownload";
import { useLatestDownload } from "../useLatestDownload";

/** The banner is outside the two branches below, so the page has its heading —
 * and says whose case this is — whether or not the release metadata loads. */
function DownloadBanner() {
  return (
    <section className="page-banner page-banner--download marketing__bleed">
      <div className="page-header bleed-inner">
        <h1>Download Big Bear Backup for Windows</h1>
        <p className="banner-line">Put Big Bear on the case.</p>
      </div>
    </section>
  );
}

/** The commands to fetch, check and unpack one build, for whichever machine
 * runs them: `uname -m` prints `x86_64` or `aarch64`, which is how the
 * tarballs are named. `--ignore-missing`, as only one of the two builds
 * SHA256SUMS lists is downloaded. */
function installCommands(cli: LatestCliDownload): string {
  return [
    `cd "$(mktemp -d)"`,
    `TARBALL=bigbear-${cli.version}-$(uname -m)-unknown-linux-musl.tar.gz`,
    `curl -fLO ${cli.folderUrl}/$TARBALL`,
    `curl -fLO ${cli.sumsUrl}`,
    `sha256sum -c --ignore-missing SHA256SUMS`,
    `sudo mkdir -p /opt/bigbear`,
    `sudo tar -xzf $TARBALL -C /opt/bigbear --strip-components=1`,
    `/opt/bigbear/bigbear --version`,
  ].join("\n");
}

const CRON_EXAMPLE = [
  "# /etc/cron.d/bigbear: every night at 02:30, as root",
  "30 2 * * * root /opt/bigbear/bigbear backup /mnt/backup/vault --passphrase-file /root/bigbear-passphrase --job server -q /etc /home /srv",
].join("\n");

/** `bigbear`, the command line, for a Linux server (#958). Its own section,
 * after the Windows download, as it is a different thing for different
 * people: no window, no updater, run by root from cron. */
function LinuxCommandLine() {
  const cli = useLatestCliDownload();

  return (
    <section className="panel download download--cli" aria-labelledby="linux-command-line">
      <h2 id="linux-command-line">Command line for Linux</h2>
      <p>
        <code>bigbear</code>, the command line, for a Linux server: back up from cron, as root, to
        a vault on a drive, a share or a cloud bucket. One file, built static, so it runs on any
        distribution.
      </p>

      {!cli ? (
        <p className="muted">The first Linux build comes with the next release.</p>
      ) : (
        <>
          <p className="muted">Version {cli.version}</p>

          {cli.status === "building" && (
            <p className="download__building" role="status">
              v{cli.version}'s Linux builds are still being published. The links below may not work
              for a few more minutes — refresh to check.
            </p>
          )}

          <ul className="download__builds">
            {cli.builds.map((build) => (
              <li key={build.target}>
                <a href={build.url}>{build.label}</a>
                {build.sha256 && (
                  <span className="muted mono download__checksums">
                    {" "}
                    sha256: {build.sha256}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="muted download__note">
            Checksums for both: <a href={cli.sumsUrl}>SHA256SUMS</a>
          </p>

          <h3>Install</h3>
          <p className="muted download__note">
            Keep it in a folder only root can write to, such as <code>/opt/bigbear/</code>, not{" "}
            <code>/usr/local/bin</code> or a home folder. Run as root, it keeps its frozen-view
            records in a <code>frozen-views</code> folder beside itself, and a program root runs
            from cron must not sit where another account could replace it.
          </p>
          <pre className="download__commands">
            <code>{installCommands(cli)}</code>
          </pre>

          <h3>Back up from cron</h3>
          <p className="muted download__note">
            Make the vault once with <code>sudo /opt/bigbear/bigbear init /mnt/backup/vault</code>,
            which prints its recovery key once: write it down. Put the passphrase in a file only root
            can read (<code>chmod 600</code>), then schedule the backup:
          </p>
          <pre className="download__commands">
            <code>{CRON_EXAMPLE}</code>
          </pre>
          <p className="muted download__note">
            <code>bigbear help</code> lists the rest, from cloud vaults to pruning old snapshots.
          </p>
        </>
      )}
    </section>
  );
}

export function Download() {
  const download = useLatestDownload();

  if (!download) {
    return (
      <>
        <DownloadBanner />
        <div className="empty empty--error" role="alert">
          <h2>Could not load the latest release</h2>
          <p className="muted">
            No release notes are published yet — try the{" "}
            <Link to="/releases">releases page</Link>.
          </p>
        </div>
        <LinuxCommandLine />
      </>
    );
  }

  return (
    <>
      <DownloadBanner />
      <section className="panel download">
        <p className="muted">
          Version {download.version} — {download.headline} · Released {download.date} ·{" "}
          <Link to={`/releases/${download.version}`}>Full release notes →</Link>
        </p>

        {download.status === "building" && (
          <p className="download__building" role="status">
            v{download.version}'s installers are still building. The links below may still point at
            the previous release for a few more minutes — refresh to check.
          </p>
        )}

        <div className="download__actions">
          <a className="button button--primary" href={download.nsisUrl}>
            Download for Windows (recommended)
          </a>
          <a className="button" href={download.msiUrl}>
            Download MSI installer
          </a>
        </div>

        {download.msiUrl && (
          <p className="muted download__note">
            Uninstalling from the MSI installer leaves Open file backup on this PC. Uninstalling
            from the recommended installer asks whether to remove it too.
          </p>
        )}

        {(download.nsisSha256 || download.msiSha256) && (
          <p className="muted mono download__checksums">
            {download.nsisSha256 && <>installer sha256: {download.nsisSha256}</>}
            {download.nsisSha256 && download.msiSha256 && <br />}
            {download.msiSha256 && <>msi sha256: {download.msiSha256}</>}
          </p>
        )}

        <p className="muted download__note">
          Windows SmartScreen may warn on first run — the installer is signed for the
          auto-updater, but not yet with a Windows Authenticode certificate. Choose{" "}
          <strong>More info</strong> → <strong>Run anyway</strong>.
        </p>

        <p className="muted download__note">
          The app checks for updates on its own and can install them automatically or just let you
          know — your choice, in Settings once it's installed.
        </p>

        <p className="muted download__note">
          Choosing a destination? <Link to="/providers">Providers, threads and pack size</Link>{" "}
          compares what each one costs and how to tune it.
        </p>
      </section>

      <LinuxCommandLine />
    </>
  );
}
